import { statsCompression, map2obj } from '@rtcstats/rtcstats-shared';
import type { RtcStatsTrace } from '@rtcstats/rtcstats-js';
import { logTraceDisabled, logWrappedPc } from 'utils/webrtc/stats/stats-logger';
import { AggregateScores, TrackScore } from 'utils/webrtc/stats/types';

export interface StatsTraceOptions {
  rawTrace: RtcStatsTrace;
  isEmissionEnabled: () => boolean;
  maxPreConnectFrames?: number;
}

export interface TraceScoresPayload {
  trackScores: TrackScore[];
  aggregate: AggregateScores;
}

interface PcCompressionState {
  /** The wrapper-assigned `PC_n` id; a change signals a new PC generation. */
  pcId: string;
  /** Deep clone of the previous tick's full `map2obj(report)` — the delta base. */
  prevStats: object;
  /** One id map accumulated across the PC's whole life (never recreated per tick). */
  statsIdMap: object;
}

// Guards against unbounded heap growth when the flag is on but the socket never opens.
// The legitimate pre-connect window is tens of frames (create + SDP/ICE for ≤2 PCs).
const DEFAULT_MAX_PRE_CONNECT_FRAMES = 2_000;

/**
 * Session-scoped wrapper around the library's WebSocketTrace. Outlives the
 * StatsOrchestrator so the socket spans true session boundaries, surviving
 * media reconnects.
 */
export class StatsTrace {
  private readonly rawTrace: RtcStatsTrace;

  private readonly isEmissionEnabled: () => boolean;

  private readonly maxPreConnectFrames: number;

  private readonly pcStates = new Map<string, PcCompressionState>();

  /** pcIds seen on the current socket's stream; moved to dead on close(). */
  private readonly livePcIds = new Set<string>();

  /** pcIds from generations that predate a close(); their frames never flow again. */
  private readonly deadPcIds = new Set<string>();

  private connected = false;

  /** Set once the library throws — it keeps throwing when a page script replaced RTCPeerConnection or WebSocket. */
  private disabled = false;

  private preConnectFrames = 0;

  /** Bound once so the library and orchestrator share the same reference (used by `wrapRTCPeerConnection`). */
  readonly trace: (...args: unknown[]) => void;

  constructor(opts: StatsTraceOptions) {
    this.rawTrace = opts.rawTrace;
    this.isEmissionEnabled = opts.isEmissionEnabled;
    this.maxPreConnectFrames = opts.maxPreConnectFrames ?? DEFAULT_MAX_PRE_CONNECT_FRAMES;
    this.trace = this.emit.bind(this);
  }

  private emit(...args: unknown[]): void {
    // A PC created while gated loses its create frame; later frames would be
    // orphaned and the importer cannot load a dump with no create.
    if (!this.isActive()) {
      return;
    }
    // The orchestrator-fed getStats has 3 args (method, pcId, data); the
    // wrapper's connect-time one-shot adds a 4th `reason` arg. Drop only that.
    if (args[0] === 'getStats' && args.length === 4) {
      return;
    }
    const pcId = args[1];
    if (typeof pcId === 'string') {
      // Post-close frames are buffered by the library and flushed ahead of the
      // next dump's create frame, crashing the importer. pcIds are never reused.
      if (this.deadPcIds.has(pcId)) {
        return;
      }
      this.livePcIds.add(pcId);
    }
    if (!this.connected) {
      if (this.preConnectFrames >= this.maxPreConnectFrames) {
        return;
      }
      this.preConnectFrames += 1;
    }
    try {
      this.rawTrace(...args);
    } catch (error) {
      this.disable('emit', error);
    }
  }

  /** Idempotent — WebSocketTrace.connect would reopen an existing socket, breaking the dump. */
  connect(wsUrl: string): void {
    if (this.connected || this.disabled) {
      return;
    }
    try {
      this.rawTrace.connect(wsUrl);
    } catch (error) {
      // Stays unconnected so close() never touches the half-opened socket.
      this.disable('connect', error);
      return;
    }
    this.connected = true;
  }

  close(): void {
    if (!this.connected) {
      return;
    }
    this.connected = false;
    this.preConnectFrames = 0;
    this.pcStates.clear(); // prevStats holds full-report clones; release memory on close.
    // Move all live pcIds to dead so their post-close frames never reach the next dump.
    this.livePcIds.forEach((id) => this.deadPcIds.add(id));
    this.livePcIds.clear();
    this.rawTrace.close();
  }

  /** Mirrors the library's polling recipe (no second poll needed); returns the capture cost in ms (map2obj + clone + compression), or 0 when gated. */
  feedGetStats(pcKey: string, pcId: string, report: RTCStatsReport): number {
    // Skip the compression cost entirely when gated (not just the wire send).
    if (!this.isActive()) {
      return 0;
    }
    // Dead generation: skip compression cost (emit() would drop the frame anyway)
    // and avoid the spurious new-generation log that pcStates.clear() in close() causes.
    if (this.deadPcIds.has(pcId)) {
      return 0;
    }
    // Detect before stateFor mutates the map, so the log fires once per generation.
    const isNewPcGeneration = this.pcStates.get(pcKey)?.pcId !== pcId;
    const state = this.stateFor(pcKey, pcId);
    if (isNewPcGeneration) {
      logWrappedPc({ pcKey, pcId });
    }
    const startedAt = performance.now();
    const stats = map2obj(report);
    // Mirrors the library's peerconnection.js recipe so the wire stream matches
    // wrapper-polled capture. Deep-clone cost (statsCompression also clones) is
    // measured via the return value.
    const baseStats = JSON.parse(JSON.stringify(stats));
    const compressed = statsCompression(state.prevStats, stats, state.statsIdMap);
    const costMs = performance.now() - startedAt;
    this.trace('getStats', pcId, compressed);
    state.prevStats = baseStats;
    return costMs;
  }

  traceScores(payload: TraceScoresPayload): void {
    this.trace(':samba-track-scores', null, payload);
  }

  /** Drop all per-PC compression state on a breakout room change. */
  reset(): void {
    this.pcStates.clear();
  }

  private isActive(): boolean {
    return !this.disabled && this.isEmissionEnabled();
  }

  private disable(stage: 'connect' | 'emit', error: unknown): void {
    this.disabled = true;
    logTraceDisabled(stage, error);
  }

  private stateFor(pcKey: string, pcId: string): PcCompressionState {
    const existing = this.pcStates.get(pcKey);
    if (existing && existing.pcId === pcId) {
      return existing;
    }
    // Fresh baseline on first feed or PC-identity change; the wrapper auto-emits the create frame.
    const fresh: PcCompressionState = { pcId, prevStats: {}, statsIdMap: {} };
    this.pcStates.set(pcKey, fresh);
    return fresh;
  }
}
